import { FC } from 'react'
import { FilterPeriod } from 'src/constants/filterPeriods/enums/filterPeriod.enum'
import { AnalyticsFetchCampaignsCalendarResponse } from 'src/services/__generated__/api'
import { ReactComponent as EmptyStateChartIcon } from 'src/assets/svg/empty-state-chart.svg'
import { numberWithCommas } from 'src/utils/numberFormat'
import { Skeleton } from 'src/components/Skeleton'
import styles from './mentionsByMonth.module.scss'

// Real dimensions from mentionsByMonth.module.scss: .gridArea is a fixed 332px
// tall, which is exactly 7 rows of 44px .cell plus six 4px gaps — so the grid
// itself cannot shift and the skeleton just fills the space it already owns.
// The .colHeaders row above it is NOT fixed: it collapses to 0 while xAxis is
// empty and pushes everything down ~16px when the labels arrive, so it gets a
// skeleton sized to the 14px label text.
const SKELETON_ROW_COUNT = 7
// Column count is unknown before the data lands (it varies with the period).
// 8 matches the widest the 1010px cell track fits at the 115px .cell width, and
// only the column WIDTHS differ from the real render — the heights, which are
// what could shift, are exact.
const SKELETON_COLUMN_COUNT = 8
const SKELETON_CELL_HEIGHT = 44
const SKELETON_LABEL_HEIGHT = 16
const SKELETON_COL_HEADER_WIDTH = 40
const SKELETON_ROW_LABEL_WIDTH = 36

interface MentionsByMonthProps {
  chartData?: AnalyticsFetchCampaignsCalendarResponse
  isLoading?: boolean
  period?: FilterPeriod | string
}

const getTitle = (period?: string): string => {
  switch (period) {
    case FilterPeriod.TODAY:
      return 'Mentions by Day'
    case FilterPeriod.YESTERDAY:
      return 'Mentions by Yesterday'
    case FilterPeriod.LAST_7_DAYS:
      return 'Mentions by Last 7 Days'
    case FilterPeriod.LAST_30_DAYS:
      return 'Mentions by Last 30 Days'
    case FilterPeriod.LAST_90_DAYS:
      return 'Mentions by Last 90 Days'
    case FilterPeriod.LIFETIME:
      return 'Mentions by Lifetime'
    case FilterPeriod.CUSTOM:
      return 'Mentions by Custom Range'
    default:
      return 'Mentions'
  }
}

/**
 * Returns a background color for a cell based on sentimentScore (item[3]).
 * Positive scale: low → high opacity purple/blue
 * Negative scale: low → high opacity orange/red
 * Near zero: light neutral
 */
const getCellColor = (score: number, mentionCount: number): string => {
  if (!mentionCount) return 'transparent'
  if (score >= 7.5) return '#6C71E6'
  if (score >= 5) return '#8080FFCC'
  if (score >= 2.5) return '#6C71E699'
  if (score > 0) return '#6C71E633'
  if (score === 0) return '#F3F5F5'
  if (score > -2.5) return '#FFD2C2'
  if (score > -5) return '#FFA485'
  if (score > -7.5) return '#FF6833CC'
  return '#FF6833'
}

const getCellTextColor = (score: number, mentionCount: number): string => {
  if (!mentionCount) return 'transparent'
  // Dark text on light cells, white on dark cells
  if (Math.abs(score) >= 5) return '#ffffff'
  return '#25262d'
}

export const MentionsByMonth: FC<MentionsByMonthProps> = ({
  chartData,
  isLoading,
  period,
}) => {
  const xAxis = chartData?.xAxis ?? []
  const yAxis = chartData?.yAxis ?? []
  const sentiments = chartData?.sentiments ?? []

  // Build lookup: [xIdx][yIdx] = { count, score }
  const cellMap: Record<string, { count: number; score: number }> = {}
  sentiments.forEach((item) => {
    const xIdx = item[0]
    const yIdx = item[1]
    const count = item[2] ?? 0
    const score = item[3] ?? 0
    cellMap[`${xIdx}-${yIdx}`] = { count, score }
  })

  // "loading" and "no data" are separate states. `hasData` used to fold
  // isLoading in, which meant the data branch rendered with empty axes during
  // the first load: an axis-less, cell-less grid asserting a shape it did not
  // have yet.
  const hasData = sentiments.length > 0

  // Skeletons are for the FIRST load only — a refetch keeps the existing grid
  // on screen rather than flashing it back to placeholders.
  const isInitialLoading = !!isLoading && !chartData

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>{getTitle(period)}</h3>
      </div>

      {/* Loading — a branch of its own, BEFORE the empty state, so the widget
          never claims "no data" for data that has not arrived yet. */}
      {isInitialLoading ? (
        <>
          <div
            className={styles.colHeaders}
            style={{
              gridTemplateColumns: `78px repeat(${SKELETON_COLUMN_COUNT}, 1fr)`,
            }}
          >
            <div />
            {Array.from({ length: SKELETON_COLUMN_COUNT }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={styles.colHeader} key={i}>
                <Skeleton
                  height={SKELETON_LABEL_HEIGHT}
                  radius={4}
                  width={SKELETON_COL_HEADER_WIDTH}
                />
              </div>
            ))}
          </div>

          <div className={styles.gridArea}>
            <div className={styles.rowLabels}>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={styles.rowLabel} key={i}>
                  <Skeleton
                    height={SKELETON_LABEL_HEIGHT}
                    radius={4}
                    width={SKELETON_ROW_LABEL_WIDTH}
                  />
                </div>
              ))}
            </div>

            <div
              className={styles.grid}
              style={{
                gridTemplateColumns: `repeat(${SKELETON_COLUMN_COUNT}, 1fr)`,
              }}
            >
              {Array.from({
                length: SKELETON_ROW_COUNT * SKELETON_COLUMN_COUNT,
              }).map((_, i) => (
                <Skeleton
                  height={SKELETON_CELL_HEIGHT}
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  radius={4}
                  width="100%"
                />
              ))}
            </div>
          </div>

          {/* The legend is static chrome, not data — render it as-is so it does
              not arrive late and shove the grid around. */}
          <div className={styles.legendWrapper}>
            <div className={styles.legendPill}>
              <span className={styles.legendArrow}>&#8249;</span>
              <div className={styles.legendGradient} />
              <span className={styles.legendArrow}>&#8250;</span>
            </div>
            <div className={styles.legendLabels}>
              <span className={styles.legendLabel}>Positive</span>
              <span className={styles.legendLabel}>Negative</span>
            </div>
          </div>
        </>
      ) : !hasData ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <EmptyStateChartIcon className={styles.emptyIconSvg} />
          </div>
          <h4 className={styles.emptyTitle}>{getTitle(period)}</h4>
          <p className={styles.emptySubtext}>
            There&apos;s not enough data yet to generate {getTitle(period)}.
            <br />
            Please check back later.
          </p>
        </div>
      ) : (
        <>
          {/* Column headers row */}
          <div
            className={styles.colHeaders}
            style={{ gridTemplateColumns: `78px repeat(${xAxis.length}, 1fr)` }}
          >
            <div />
            {xAxis.map((label, i) => (
              <div className={styles.colHeader} key={i}>
                {label}
              </div>
            ))}
          </div>

          {/* Grid area */}
          <div className={styles.gridArea}>
            <div className={styles.rowLabels}>
              {yAxis.map((label, i) => (
                <div className={styles.rowLabel} key={i}>
                  {label}
                </div>
              ))}
            </div>

            <div
              className={styles.grid}
              style={{ gridTemplateColumns: `repeat(${xAxis.length}, 1fr)` }}
            >
              {yAxis.map((_, yIdx) =>
                xAxis.map((_, xIdx) => {
                  const cell = cellMap[`${xIdx}-${yIdx}`]
                  const count = cell?.count ?? 0
                  const score = cell?.score ?? 0
                  const bg = getCellColor(score, count)
                  const color = getCellTextColor(score, count)
                  return (
                    <div
                      className={styles.cell}
                      key={`${xIdx}-${yIdx}`}
                      style={{ background: bg, color }}
                    >
                      {count > 0 ? numberWithCommas(count) : ''}
                    </div>
                  )
                })
              )}
            </div>
          </div>

          {/* Legend */}
          <div className={styles.legendWrapper}>
            <div className={styles.legendPill}>
              <span className={styles.legendArrow}>&#8249;</span>
              <div className={styles.legendGradient} />
              <span className={styles.legendArrow}>&#8250;</span>
            </div>
            <div className={styles.legendLabels}>
              <span className={styles.legendLabel}>Positive</span>
              <span className={styles.legendLabel}>Negative</span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
